/**
 * What a hole's score WAS, against par — THE classifier for the client.
 *
 *   ≤ −2  eagle   (anything better than an eagle counts as one)
 *   −1    birdie
 *    0    par
 *   +1    bogey
 *   ≥ +2  double  (double bogey or worse)
 *
 * Every client surface that sorts a hole into one of those five buckets reads
 * it from here: the score markers (`components/ScoreMarker.js`), the five-count
 * strip (`components/ScoreSummary.js`), the competition matrix, the team review
 * grid, the presentation screen and the PDF report. It used to be written out
 * inline in seven files, which is how the leaderboard band came to count a
 * double as a bogey while the card above it drew the two differently.
 *
 * The server has the same rule in ONE place too
 * (`services/scoring_math.py::score_result`) — the birdie push, the round-stats
 * band and the scoring insights all read that. The two cannot share code, so
 * `scoreResult.test.js` and `test_score_result.py` pin the same table.
 */

export const SCORE_RESULTS = ['eagle', 'birdie', 'par', 'bogey', 'double'];

/** The bucket for a score already expressed against par, or null. */
export const resultForToPar = (toPar) => {
  if (toPar === null || toPar === undefined || Number.isNaN(toPar)) return null;
  if (toPar <= -2) return 'eagle';
  if (toPar === -1) return 'birdie';
  if (toPar === 0) return 'par';
  if (toPar === 1) return 'bogey';
  return 'double';
};

/**
 * The bucket for `strokes` on a hole of `par`, or null when the hole cannot be
 * judged — not played (no strokes, or 0) or no par to judge it against.
 * Strings are accepted because the scoring grids hold their entries as typed.
 */
export const scoreResult = (strokes, par) => {
  const s = parseInt(strokes, 10);
  const p = parseInt(par, 10);
  if (!s || !p) return null;
  return resultForToPar(s - p);
};

/** Spoken names, for aria-labels. */
export const SCORE_RESULT_NAMES = {
  eagle: 'eagle or better',
  birdie: 'birdie',
  par: 'par',
  bogey: 'bogey',
  double: 'double bogey or worse',
};

/** An empty tally, one count per bucket. */
export const emptyCounts = () => ({ eagle: 0, birdie: 0, par: 0, bogey: 0, double: 0 });

/**
 * Tally `[{ strokes, par }]` into the five buckets. A hole that cannot be
 * judged is skipped, never counted as anything.
 */
export const countResults = (holes) => {
  const counts = emptyCounts();
  (holes || []).forEach(({ strokes, par }) => {
    const r = scoreResult(strokes, par);
    if (r) counts[r] += 1;
  });
  return counts;
};
